import * as R from 'ramda';
import { CustomError, TechnicalError } from '../../../common/errors';
import Result from '../../../common/result';
import { Credentials, Evidence } from '../../../common/types';
import sip, { ScopeRequest } from '../../../services/secureIdentityProvider';
import { EvidenceChannel, IdentityComponent } from '../../../services/secureIdentityProvider/types';

export const uploadEvidence = async (
  scopeRequestId: string,
  identity: IdentityComponent,
  evidence: Evidence,
): Promise<Result<CustomError>> => {
  const evidenceChannel = R.pathOr<EvidenceChannel | null>(null, [
    'request',
    'payload',
    'channels',
    'evidences',
    evidence.content,
  ])(identity);
  if (!evidenceChannel) {
    return Result.failed(
      new TechnicalError(
        `Unable to upload evidence for scope request ${scopeRequestId}, no evidence channel found for: ${evidence.content}`,
      ),
    );
  }

  return sip.uploadEvidence({
    evidenceToUpload: evidence,
    evidenceChannel,
    scopeRequestId,
  });
};

export const resolveScopeRequestEvidences = async (
  scopeRequestId: string,
  identity: IdentityComponent,
  evidences: Evidence[],
): Promise<Result<CustomError>> => {
  if (R.isNil(evidences) || R.isEmpty(evidences)) {
    return Result.noValueSuccess();
  }

  const uploads: Promise<Result<CustomError>>[] = evidences.map((evidence) =>
    uploadEvidence(scopeRequestId, identity, evidence),
  );

  const uploadResults = (await Promise.all(uploads)).map((result) => {
    // log upload error if applicable
    result.logError(`Error uploading evidence for scope request: ${scopeRequestId}`);
    return result.isFailed();
  });
  return R.any(R.identity, uploadResults)
    ? Result.noValueFailed(new TechnicalError('Unexpected error uploading evidences'))
    : Result.noValueSuccess();
};

const resolveScopeRequest = async (
  scopeRequest: ScopeRequest,
  credentials: Credentials,
): Promise<Result<CustomError, ScopeRequest>> => {
  const { identity } = scopeRequest.components;
  if (!identity) {
    return Result.failed(
      new TechnicalError(`Unable to resolve scope request without identity component: ${scopeRequest.uuid}`),
    );
  }

  // process evidence upload first
  const evidenceResult = await resolveScopeRequestEvidences(scopeRequest.uuid, identity, credentials.evidences);
  if (evidenceResult.isFailed()) {
    return Result.failed(
      new TechnicalError(`Error uploading evidences for scope request: ${scopeRequest.uuid}`, evidenceResult.failure()),
    );
  }

  const resolveResult = await sip.resolveScopeRequest({
    scopeRequestId: scopeRequest.uuid,
    components: {
      identity: {
        response: credentials.credentials,
      },
    },
  });

  if (resolveResult.isFailed()) {
    return Result.failed(
      new TechnicalError(`Error resolving scope request: ${scopeRequest.uuid}`, resolveResult.failure()),
    );
  }

  const updatedScopeRequest = resolveResult.success();
  return Result.successful(updatedScopeRequest);
};

export default {
  resolveScopeRequest,
};
